<template>
  <header>
    <!-- Navigation Bar -->
    <div
      ref="navbar"
      class="fixed top-0 left-0 w-full z-50 transition-all duration-300 ease-in-out"
      :class="navbarClasses"
    >
      <div
        class="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex flex-wrap justify-between items-center py-4"
      >
        <!-- Logo -->
        <div class="flex items-center">
          <router-link to="/" class="group">
            <div class="flex items-center space-x-3">
              <div
                class="w-12 h-12 rounded-full flex items-center justify-center shadow-lg group-hover:shadow-purple-500/25 transition-all duration-300"
                :class="logoClasses"
              >
                <span class="font-bold text-xl" :class="logoTextClasses"
                  >M</span
                >
              </div>
              <span
                class="text-xl font-semibold tracking-wide"
                :class="textClasses"
                >Mshel Foundation</span
              >
            </div>
          </router-link>
        </div>

        <!-- Mobile Menu Toggle -->
        <div class="lg:hidden ml-auto">
          <button
            @click="toggleMenu"
            class="relative w-10 h-10 focus:outline-none group"
            :class="{ active: isMenuOpen }"
          >
            <div
              class="absolute inset-0 rounded-lg backdrop-blur-sm group-hover:bg-opacity-20 transition-all duration-300"
              :class="mobileButtonBgClasses"
            ></div>
            <div
              class="relative flex flex-col items-center justify-center w-full h-full"
            >
              <span
                class="block w-5 h-0.5 transform transition-all duration-300"
                :class="[
                  hamburgerClasses,
                  isMenuOpen ? 'rotate-45 translate-y-0.5' : '',
                ]"
              ></span>
              <span
                class="block w-5 h-0.5 mt-1 transition-all duration-300"
                :class="[hamburgerClasses, isMenuOpen ? 'opacity-0' : '']"
              ></span>
              <span
                class="block w-5 h-0.5 mt-1 transform transition-all duration-300"
                :class="[
                  hamburgerClasses,
                  isMenuOpen ? '-rotate-45 -translate-y-1.5' : '',
                ]"
              ></span>
            </div>
          </button>
        </div>

        <!-- Desktop Navigation -->
        <nav class="hidden lg:flex items-center space-x-8">
          <router-link
            to="/"
            class="transition-all duration-300 font-medium relative group"
            :class="linkClasses"
          >
            Home
            <span
              class="absolute -bottom-1 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
              :class="underlineClasses"
            ></span>
          </router-link>

          <router-link
            to="/about-us"
            class="transition-all duration-300 font-medium relative group"
            :class="linkClasses"
          >
            About
            <span
              class="underline-bar absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow group-hover:w-full transition-all duration-300"
              :class="underlineClasses"
            ></span>
          </router-link>

          <router-link
            to="/our-impact"
            class="transition-all duration-300 font-medium relative group"
            :class="linkClasses"
          >
            Our Impact
            <span
              class="underline-bar absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow group-hover:w-full transition-all duration-300"
              :class="underlineClasses"
            ></span>
          </router-link>

          <!-- Dropdown -->
          <div class="relative group">
            <div
              class="px-4 py-2 inline-flex items-center transition-all duration-300 font-medium cursor-pointer group"
              :class="linkClasses"
            >
              Programs
              <svg
                class="w-4 h-4 ml-2 transform group-hover:rotate-180 transition-transform duration-300"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 10 6"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m1 1 4 4 4-4"
                />
              </svg>
            </div>
            <div
              class="invisible group-hover:visible opacity-0 group-hover:opacity-100 absolute left-0 top-full w-56 backdrop-blur-lg border rounded-xl shadow-2xl transition-all duration-300 transform translate-y-2 group-hover:translate-y-0"
              :class="dropdownClasses"
            >
              <div class="p-2">
                <router-link
                  to="/pitch-your-dream"
                  class="flex items-center px-4 py-3 rounded-lg transition-all duration-200 group/item"
                  :class="dropdownItemClasses"
                >
                  <div
                    class="w-8 h-8 bg-yellow rounded-lg flex items-center justify-center mr-3 group-hover/item:scale-110 transition-transform duration-200"
                  >
                    <svg
                      class="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.84L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.84l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"
                      />
                    </svg>
                  </div>
                  <div>
                    <div class="font-medium">Pitch Your Dream</div>
                  </div>
                </router-link>
              </div>
              <div class="p-2">
                <router-link
                  to="/scholarship"
                  class="flex items-center px-4 py-3 rounded-lg transition-all duration-200 group/item"
                  :class="dropdownItemClasses"
                >
                  <div
                    class="w-8 h-8 bg-yellow rounded-lg flex items-center justify-center mr-3 group-hover/item:scale-110 transition-transform duration-200"
                  >
                    <svg
                      class="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.84L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.84l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"
                      />
                    </svg>
                  </div>
                  <div>
                    <div class="font-medium">Scholarship</div>
                  </div>
                </router-link>
              </div>
            </div>
          </div>

          <router-link
            to="/contact-us"
            class="transition-all duration-300 font-medium relative group"
            :class="linkClasses"
          >
            Contact
            <span
              class="absolute -bottom-1 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
              :class="underlineClasses"
            ></span>
          </router-link>

          <!-- Donate Button -->
          <router-link to="/donate">
            <button
              class="relative px-6 py-3 font-semibold rounded-full shadow-lg transform hover:scale-105 transition-all duration-300 overflow-hidden group"
              :class="donateButtonClasses"
            >
              <span class="relative z-10 flex items-center">
                <svg
                  class="w-4 h-4 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                    clip-rule="evenodd"
                  />
                </svg>
                Donate
              </span>
              <div
                class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                :class="donateButtonHoverClasses"
              ></div>
            </button>
          </router-link>
        </nav>
      </div>

      <!-- Mobile Menu -->
      <div
        v-if="isMenuOpen"
        class="lg:hidden backdrop-blur-lg border-t w-full"
        :class="mobileMenuClasses"
      >
        <div class="px-6 py-6 space-y-1">
          <router-link
            to="/"
            class="block px-4 py-3 rounded-lg transition-all duration-200 font-medium"
            :class="mobileMenuItemClasses"
            @click="toggleMenu"
            >Home</router-link
          >
          <router-link
            to="/about-us"
            class="block px-4 py-3 rounded-lg transition-all duration-200 font-medium"
            :class="mobileMenuItemClasses"
            @click="toggleMenu"
            >About</router-link
          >
          <router-link
            to="/scholarship"
            class="block px-4 py-3 rounded-lg transition-all duration-200 font-medium"
            :class="mobileMenuItemClasses"
            @click="toggleMenu"
            >Scholarship Program</router-link
          >
          <router-link
            to="/contact-us"
            class="block px-4 py-3 rounded-lg transition-all duration-200 font-medium"
            :class="mobileMenuItemClasses"
            @click="toggleMenu"
            >Contact</router-link
          >
          <div class="pt-4">
            <router-link to="/donate" @click="toggleMenu">
              <button
                class="w-full px-6 py-3 text-white font-semibold rounded-full shadow-lg transform hover:scale-105 transition-all duration-300"
                :class="mobileDonateButtonClasses"
              >
                Donate Now
              </button>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Background detection canvas (invisible) -->
    <canvas
      ref="detectionCanvas"
      class="fixed top-0 left-0 pointer-events-none opacity-0 z-[-1]"
      width="1"
      height="1"
    ></canvas>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();
const isMenuOpen = ref(false);
const isLightBackground = ref(false);
const navbar = ref(null);
const detectionCanvas = ref(null);
const isScrolled = ref(false);

const toggleMenu = () => (isMenuOpen.value = !isMenuOpen.value);

// Enhanced background detection
const detectBackground = () => {
  try {
    const detectionPoints = [
      { x: window.innerWidth * 0.2, y: 85 },
      { x: window.innerWidth * 0.5, y: 85 },
      { x: window.innerWidth * 0.8, y: 85 },
    ];

    let lightCount = 0;

    detectionPoints.forEach((point) => {
      const elements = document.elementsFromPoint(point.x, point.y);
      let targetElement = null;

      for (const el of elements) {
        if (
          !navbar.value?.contains(el) &&
          !el.classList.contains("fixed") &&
          el.tagName !== "CANVAS"
        ) {
          targetElement = el;
          break;
        }
      }

      if (targetElement) {
        const isLight = analyzeElementBackground(targetElement);
        if (isLight) lightCount++;
      }
    });

    const shouldBeDark = lightCount > detectionPoints.length / 2;
    const scrollY = window.scrollY;
    isScrolled.value = scrollY > 50;
    isLightBackground.value = shouldBeDark;
  } catch (error) {
    console.warn("Background detection error:", error);
    isLightBackground.value = checkRouteBackground() || isScrolled.value;
  }
};

const analyzeElementBackground = (element) => {
  const styles = window.getComputedStyle(element);
  const bgColor = styles.backgroundColor;
  const bgImage = styles.backgroundImage;

  if (bgColor && bgColor !== "rgba(0, 0, 0, 0)" && bgColor !== "transparent") {
    return isLightColor(bgColor);
  }

  if (bgImage && bgImage !== "none") {
    const hasLightImage =
      bgImage.includes("white") ||
      bgImage.includes("light") ||
      element.classList.toString().includes("light") ||
      element.classList.toString().includes("white");
    if (hasLightImage) return true;
  }

  let parent = element.parentElement;
  let depth = 0;
  while (parent && depth < 5) {
    const parentStyles = window.getComputedStyle(parent);
    const parentBg = parentStyles.backgroundColor;

    if (
      parentBg &&
      parentBg !== "rgba(0, 0, 0, 0)" &&
      parentBg !== "transparent"
    ) {
      return isLightColor(parentBg);
    }

    parent = parent.parentElement;
    depth++;
  }

  const elementClasses = element.className.toLowerCase();
  const lightKeywords = [
    "white",
    "light",
    "bg-white",
    "bg-gray-50",
    "bg-gray-100",
    "bg-slate-50",
  ];
  const darkKeywords = [
    "dark",
    "black",
    "bg-black",
    "bg-gray-900",
    "bg-slate-900",
  ];

  if (lightKeywords.some((keyword) => elementClasses.includes(keyword)))
    return true;
  if (darkKeywords.some((keyword) => elementClasses.includes(keyword)))
    return false;

  return false;
};

const isLightColor = (color) => {
  if (!color || color === "transparent" || color === "rgba(0, 0, 0, 0)")
    return false;

  let r, g, b;
  if (color.startsWith("rgb")) {
    const matches = color.match(/\d+/g);
    if (matches && matches.length >= 3) {
      r = parseInt(matches[0]);
      g = parseInt(matches[1]);
      b = parseInt(matches[2]);
    }
  } else if (color.startsWith("#")) {
    const hex = color.substring(1);
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  } else if (color === "white") return true;
  else if (color === "black") return false;

  if (r !== undefined && g !== undefined && b !== undefined) {
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.7;
  }

  return false;
};

const checkRouteBackground = () => {
  const routeName = route.name || route.path;
  const lightRoutes = ["/about-us", "/contact-us", "/scholarship"];

  return lightRoutes.some(
    (lightRoute) =>
      routeName.includes(lightRoute) || route.path.includes(lightRoute)
  );
};

const navbarClasses = computed(() => {
  const base = "backdrop-blur-md";

  if (isLightBackground.value) {
    // Solid background only when on white/light background
    return `${base} bg-slate-900 bg-opacity-100 border-b border-slate-200 shadow-md`;
  }

  // Transparent for all other (non-white) backgrounds
  return `${base} bg-transparent border-b border-gray-100 border-opacity-20`;
});

const textClasses = computed(() => "text-white");
const logoClasses = computed(() => "bg-yellow");
const logoTextClasses = computed(() => "text-white");
const linkClasses = computed(() => "text-white hover:text-yellow");
const underlineClasses = computed(() => "bg-yellow");
const hamburgerClasses = computed(() => "bg-white");

const mobileButtonBgClasses = computed(() =>
  isLightBackground.value || isScrolled.value
    ? "bg-white bg-opacity-20"
    : "bg-white bg-opacity-10"
);

const dropdownClasses = computed(() =>
  isLightBackground.value || isScrolled.value
    ? "bg-slate-900 bg-opacity-95 border-slate-700"
    : "bg-white bg-opacity-10 border-white border-opacity-20"
);

const dropdownItemClasses = computed(
  () => "text-white hover:bg-white hover:bg-opacity-10"
);

const donateButtonClasses = computed(
  () => "bg-yellow text-white hover:shadow-yellow/25"
);

const donateButtonHoverClasses = computed(() => "bg-yellow");

const mobileMenuClasses = computed(() =>
  isLightBackground.value || isScrolled.value
    ? "bg-slate-900 bg-opacity-95 border-slate-700"
    : "bg-black bg-opacity-40 border-white border-opacity-10"
);

const mobileMenuItemClasses = computed(
  () => "text-white hover:bg-white hover:bg-opacity-10"
);

const mobileDonateButtonClasses = computed(
  () => "bg-gradient-to-r from-purple-500 to-pink-500"
);

// Route change handler (now used)
const handleRouteChange = () => {
  setTimeout(() => {
    detectBackground();
  }, 100);
};

// Setup detection
let rafId = null;
let scrollTimeout = null;

const setupDetection = () => {
  const handleScroll = () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      detectBackground();
    }, 16);
  };

  const handleResize = () => {
    detectBackground();
  };

  setTimeout(detectBackground, 200);

  window.addEventListener("scroll", handleScroll, { passive: true });
  window.addEventListener("resize", handleResize, { passive: true });

  const interval = setInterval(detectBackground, 1000);

  return () => {
    clearTimeout(scrollTimeout);
    clearInterval(interval);
    if (rafId) cancelAnimationFrame(rafId);
    window.removeEventListener("scroll", handleScroll);
    window.removeEventListener("resize", handleResize);
  };
};

// ✅ Watch for route changes using the function (fixes the warning)
watch(() => route.path, handleRouteChange);

onMounted(() => {
  const cleanup = setupDetection();
  onUnmounted(() => {
    cleanup();
  });
});
</script>

<style scoped lang="scss">
.backdrop-blur-md {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.backdrop-blur-lg {
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

* {
  transition: background-color 0.3s ease, color 0.3s ease,
    border-color 0.3s ease, box-shadow 0.3s ease;
}

.fixed {
  transform: translateZ(0);
  will-change: background-color, border-color;
}

// active link styles
.router-link-exact-active {
  color: #e89e1c !important;
  font-weight: 600;
}
</style>
